import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Ticket, QrCode, CheckCircle, MagnifyingGlass, ArrowsClockwise,
  CurrencyInr, Funnel, Scan, X, NavigationArrow, MapPin, Clock,
} from "@phosphor-icons/react";

const STATUS_TABS = [
  { k: "all", label: "All" },
  { k: "issued", label: "Active" },
  { k: "validated", label: "Validated" },
];

export default function QRTickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [scanOpen, setScanOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: tk }, { data: st }] = await Promise.all([
        api.get(`/admin/qr-tickets?status=${status}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/qr-tickets/stats"),
      ]);
      setTickets(tk);
      setStats(st);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const search = (e) => { e?.preventDefault(); load(); };

  const validate = async (ticketNo) => {
    try {
      const { data } = await api.post(`/admin/qr-tickets/${ticketNo}/validate`);
      if (data.already_validated) {
        toast.warning(`Ticket ${ticketNo} was already validated`);
      } else {
        toast.success(`Validated · ${data.ticket?.route_name || ticketNo}`);
        // Present only on the Sunmi scanner's companion app (window.AndroidPrint) —
        // a no-op everywhere else, including regular browsers.
        if (window.AndroidPrint?.printReceipt && data.ticket) {
          try { window.AndroidPrint.printReceipt(JSON.stringify(data.ticket)); } catch { /* printing is best-effort */ }
        }
      }
      load();
      if (detail?.ticket_no === ticketNo) setDetail({ ...detail, status: "validated", validated_at: data.ticket.validated_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    }
  };

  return (
    <div data-testid="qr-tickets-root" className="p-4 lg:p-6 space-y-4 lg:space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Customer Bookings</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">QR Bus Booking</h1>
          <p className="text-sm text-slate-500 mt-0.5">All tickets booked via MajheApp · scan or validate from here</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="qr-scan-btn" onClick={() => setScanOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#5B2EE5] hover:bg-[#4920C2] text-white text-xs font-semibold transition-colors">
            <Scan size={14} weight="bold"/> Scan / Validate
          </button>
          <button onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-semibold">
            <ArrowsClockwise size={14}/> Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="qr-stats-strip">
          <StatCard label="Total tickets" value={stats.total} tone="slate"/>
          <StatCard label="Active (issued)" value={stats.issued} tone="amber"/>
          <StatCard label="Validated" value={stats.validated} tone="emerald"/>
          <StatCard label="Today's revenue" value={`₹${stats.revenue_today.toLocaleString()}`} tone="violet"/>
          <StatCard label="All-time revenue" value={`₹${stats.revenue_total.toLocaleString()}`} tone="indigo"/>
        </div>
      )}

      {/* Filters */}
      <div className="ktcl-card p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="flex gap-1 bg-slate-100 rounded-md p-1 flex-1 sm:flex-none">
          {STATUS_TABS.map((t) => (
            <button key={t.k} data-testid={`status-tab-${t.k}`}
              onClick={() => setStatus(t.k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                status === t.k ? "bg-white text-[#1E3A8A] shadow-sm" : "text-slate-600 hover:text-slate-800"
              }`}>{t.label}</button>
          ))}
        </div>
        <form onSubmit={search} className="flex items-center gap-2 flex-1">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="qr-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search ticket #, name, mobile or route"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </div>
          <button data-testid="qr-search-btn" type="submit"
            className="px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="ktcl-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Ticket #</th>
                <th className="text-left px-3 py-2.5">Route</th>
                <th className="text-left px-3 py-2.5 hidden md:table-cell">Passenger</th>
                <th className="text-left px-3 py-2.5 hidden lg:table-cell">From → To</th>
                <th className="text-right px-3 py-2.5">Fare</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && [0,1,2,3,4].map((i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td colSpan={7} className="px-3 py-3">
                    <div className="h-5 bg-slate-100 rounded animate-pulse"/>
                  </td>
                </tr>
              ))}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-500">
                  <Ticket size={32} className="mx-auto mb-2 text-slate-300"/>
                  No tickets match this filter
                </td></tr>
              )}
              {!loading && tickets.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setDetail(t)} data-testid={`qr-row-${t.ticket_no}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-mono font-bold text-[12.5px] text-slate-900">{t.ticket_no}</div>
                    <div className="text-[10px] text-slate-400">{new Date(t.issued_at).toLocaleString()}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[13px] truncate max-w-[200px]">{t.route_name}</div>
                    <div className="text-[10px] text-slate-500">{t.route_id}</div>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell">
                    <div className="text-[13px]">{t.passenger_name}</div>
                    <a href={`tel:+91${t.passenger_mobile}`} className="text-[10px] text-slate-500 hover:text-violet-700 hover:underline">+91 {t.passenger_mobile}</a>
                  </td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-[12px] text-slate-600">
                    {t.from_stop} → {t.to_stop}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-[13px]">
                    ₹{t.total_paid}
                    <div className="text-[10px] text-slate-400 font-normal">{t.passengers}p</div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <StatusPill status={t.status}/>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {t.status === "issued" ? (
                      <button data-testid={`validate-btn-${t.ticket_no}`}
                        onClick={(e) => { e.stopPropagation(); validate(t.ticket_no); }}
                        className="px-3 py-1.5 rounded-md bg-[#16A34A] hover:bg-[#15803D] text-white text-[11px] font-bold">
                        Validate
                      </button>
                    ) : (
                      <span className="text-[10px] text-emerald-600 font-semibold">Done</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && <DetailDrawer ticket={detail} onClose={() => setDetail(null)} onValidate={() => validate(detail.ticket_no)}/>}
      {scanOpen && <ScanModal onClose={() => setScanOpen(false)} onValidate={validate}/>}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const colors = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-lg p-3 border ${colors}`}>
      <div className="text-[10.5px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="font-display text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "validated"
    ? "bg-emerald-100 text-emerald-700"
    : status === "expired"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {status === "validated" ? "Validated" : status === "issued" ? "Active" : status}
    </span>
  );
}

function DetailDrawer({ ticket, onClose, onValidate }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(ticket.qr_payload)}`;
  const validated = ticket.status === "validated";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6 bg-black/40"
      onClick={onClose} data-testid="qr-detail-drawer">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Ticket Detail</div>
            <div className="font-bold text-slate-900">{ticket.ticket_no}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="relative flex flex-col items-center bg-slate-50 rounded-lg py-4">
            <img src={qrSrc} alt="QR" className="w-44 h-44 rounded-md border border-slate-200" data-testid="detail-qr-img"
              style={validated ? { filter: "grayscale(1) opacity(0.35)" } : {}}/>
            {validated && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-red-600 text-2xl font-black tracking-[0.3em] rotate-[-12deg] border-4 border-red-600 px-3 py-1 rounded">
                  VALIDATED
                </span>
              </div>
            )}
            <div className="text-[10px] font-mono text-slate-500 mt-2 break-all px-4 text-center">{ticket.qr_payload}</div>
          </div>

          <div className="grid grid-cols-2 gap-2.5 text-[12px]">
            <Field icon={<NavigationArrow size={12} weight="fill"/>} label="From" value={ticket.from_stop}/>
            <Field icon={<MapPin size={12} weight="fill"/>} label="To" value={ticket.to_stop}/>
            <Field label="Passenger" value={ticket.passenger_name}/>
            <Field label="Mobile" value={`+91 ${ticket.passenger_mobile}`}/>
            <Field label="Route" value={ticket.route_name}/>
            <Field label="Passengers" value={ticket.passengers}/>
            <Field label="Fare" value={`₹${ticket.total_paid}`} highlight/>
            <Field icon={<Clock size={12}/>} label="Booked" value={new Date(ticket.issued_at).toLocaleString()}/>
            {validated && <Field label="Validated By" value={ticket.validated_by || "ETM"}/>}
            {validated && <Field label="Validated At" value={new Date(ticket.validated_at).toLocaleString()}/>}
          </div>

          {!validated ? (
            <button data-testid="detail-validate-btn" onClick={onValidate}
              className="w-full py-2.5 rounded-md bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-sm flex items-center justify-center gap-1.5">
              <CheckCircle size={16} weight="bold"/> Validate Ticket (Simulate ETM Scan)
            </button>
          ) : (
            <div className="w-full py-2.5 rounded-md bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center justify-center gap-1.5">
              <CheckCircle size={16} weight="fill"/> Already validated
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</div>
      <div className={`text-[12.5px] font-bold ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function ScanModal({ onClose, onValidate }) {
  const [ticketNo, setTicketNo] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6 bg-black/50"
      onClick={onClose} data-testid="scan-modal">
      <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
          <div className="font-bold text-slate-900">Scan / Enter Ticket #</div>
          <button onClick={onClose} className="text-slate-500"><X size={20}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="bg-violet-50 border border-violet-200 text-violet-800 rounded-md p-3 text-[11.5px]">
            <b>Ready to scan</b><br/>
            Point the handheld scanner at the customer&apos;s QR, or type the ticket number below.
          </div>
          <input data-testid="scan-input" autoFocus value={ticketNo} onChange={(e) => setTicketNo(e.target.value.toUpperCase())}
            placeholder="TKT-XXXXXXX" autoCapitalize="characters"
            className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono outline-none focus:border-[#5B2EE5]"
            onKeyDown={(e) => { if (e.key === "Enter" && ticketNo) { onValidate(ticketNo); onClose(); }}}/>
          <button data-testid="scan-submit" onClick={() => { onValidate(ticketNo); onClose(); }}
            disabled={!ticketNo.trim()}
            className="w-full py-2.5 rounded-md bg-[#5B2EE5] hover:bg-[#4920C2] disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-1.5">
            <Scan size={16} weight="bold"/> Validate
          </button>
        </div>
      </div>
    </div>
  );
}
